import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import JustificationContainer from '~/components/atom/JustificationContainer';
import {
  useGetLfaConversionsQuery,
  type GetLfaConversionFilterType,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import Loading from '~/components/atom/Loading';
import ErrorScreen from '~/components/page/ErrorScreen';
import { scrollBarStyles } from '~/components/molecule/OverflowScrollWrapper';
import EmptyStateComponent from '~/components/template/EmptyStateComponent';
import InfiniteScroll from '~/components/molecule/InfiniteScroll';
import Card from './components/Card';
import hasValue from '~/util/hasValue';
import { Heading3 } from '~/components/atom/Typography';
import TextButton from '~/components/atom/TextButton';

const text = {
  emptyStateHeader: ({
    onClick,
    hasSearchQuery,
  }: {
    onClick: () => void;
    hasSearchQuery: boolean;
  }) => (
    <JustificationContainer
      direction="column"
      align="center"
      margin={['xxs', null, null, null]}
    >
      <Heading3 margin={[null]}>
        {hasSearchQuery
          ? 'Niets gevonden, probeer een ander tabblad of'
          : 'Niets gevonden, probeer een ander tabblad :)'}
      </Heading3>
      {hasSearchQuery && (
        <TextButton
          label={'verwijder de zoekopdracht'}
          onClick={onClick}
          size="large"
        />
      )}
    </JustificationContainer>
  ),
};

export type Props = {
  dataTestId?: string;
  className?: string;
  filter: GetLfaConversionFilterType;
  selectedId: string | null;
  searchQuery: string;
  onChange: (id: string) => void;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
};

const CardsList: React.FCC<Props> = ({
  filter,
  onChange,
  searchQuery,
  selectedId,
  setSearchQuery,
}) => {
  const { id: accountId } = useCurrentAccount();
  const { data, loading, error, fetchMore } = useGetLfaConversionsQuery({
    variables: {
      accountId,
      filter,
      query: searchQuery,
    },
  });
  const baseVariables = useMemo(
    () => ({
      accountId,
      limit: 20,
    }),
    [accountId],
  );

  const nextToken = data?.getLfaConversions.nextToken;

  const loadMore = () =>
    fetchMore({
      variables: {
        ...baseVariables,
        nextToken: nextToken || null,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        const prevData = previousResult.getLfaConversions;
        const newData = fetchMoreResult.getLfaConversions;

        return {
          ...previousResult,
          getLfaConversions: {
            ...prevData,
            items: [...prevData.items, ...(newData.items || [])],
            nextToken: newData.nextToken,
          },
        };
      },
    });

  // Select conversion from a query
  useEffect(() => {
    if (hasValue(searchQuery) && data?.getLfaConversions.items.length === 1) {
      onChange(data.getLfaConversions.items[0].id);
    }
  }, [data?.getLfaConversions.items, onChange, searchQuery]);

  // Select first conversion if no conversion is selected
  useEffect(() => {
    if (data?.getLfaConversions && !loading && selectedId === null) {
      const firstItem = data.getLfaConversions.items[0];
      if (firstItem) onChange(firstItem.id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, selectedId]);

  if (loading) return <Loading />;
  if (error || !data || !data.getLfaConversions) return <ErrorScreen />;
  if (data.getLfaConversions.items.length === 0)
    return (
      <EmptyStateContainer>
        <EmptyStateComponent
          header={text.emptyStateHeader({
            onClick: () => setSearchQuery(''),
            hasSearchQuery: hasValue(searchQuery),
          })}
        />
      </EmptyStateContainer>
    );

  return (
    <Container fetchMoreFn={loadMore} hasMore={nextToken != null}>
      <ListContainer
        direction="column"
        width="100%"
        align="center"
        gap="xs"
        height="100vh"
        padding={['xxxs', 'xxs', null, 'xxs']}
      >
        {data.getLfaConversions.items.map(conversion => (
          <Card
            key={conversion.id}
            filter={filter}
            selected={selectedId === conversion.id}
            conversion={conversion}
            onClick={() => onChange(conversion.id)}
          />
        ))}
      </ListContainer>
    </Container>
  );
};

const EmptyStateContainer = styled(JustificationContainer)`
  flex-shrink: 0;
  flex-basis: auto;
  max-width: 100%;
  overflow: hidden;
`;

const ListContainer = styled(JustificationContainer)`
  overflow-y: auto;
  position: relative;
  ${scrollBarStyles}
`;

const Container = styled(InfiniteScroll)`
  width: 100%;
`;

export default CardsList;
